// frontend/modules/customer-notification/hooks/useNotificationTrigger.ts
import { useCallback, useState } from 'react';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';
import { useNotificationStore } from '../stores/notification-store';
import {NotificationPlatforms, Languages, NotificationTemplateType} from '@/shared/api/model';

// Import the API functions generated by Orval
import {
    useTriggerLoanReminders,
    useTriggerLoanOverdueNotices,
    useSendPromotionalNotifications,
    useSendGeneralMeetingNotifications,
    useSendProductChangeNotifications,
    useSendCustomBulkSms
} from '@/shared/api/client/manual-notification-trigger-controller';

// Import the notification process service controller functions
import { useExecuteNotificationProcess } from '@/shared/api/client/notification-process-service-controller';

export const useNotificationTrigger = () => {
    const { dark } = useTheme();
    const [processing, setProcessing] = useState(false);
    const [resultSummary, setResultSummary] = useState<{
        total: number;
        success: number;
        timestamp: string;
    } | null>(null);

    // Get form display state from store
    const {
        isFormVisible,
        activeFormType,
        setIsFormVisible,
        setActiveFormType
    } = useNotificationStore();

    // Get API mutations
    const { mutateAsync: triggerLoanReminders, isPending: isRemindersLoading } = useTriggerLoanReminders();
    const { mutateAsync: triggerLoanOverdue, isPending: isOverdueLoading } = useTriggerLoanOverdueNotices();
    const { mutateAsync: sendPromotional, isPending: isPromotionalLoading } = useSendPromotionalNotifications();
    const { mutateAsync: sendMeeting, isPending: isMeetingLoading } = useSendGeneralMeetingNotifications();
    const { mutateAsync: sendProductChange, isPending: isProductChangeLoading } = useSendProductChangeNotifications();
    const { mutateAsync: sendCustomSms, isPending: isCustomSmsLoading } = useSendCustomBulkSms();
    const { mutateAsync: executeNotificationProcess, isPending: isExecutingProcess } = useExecuteNotificationProcess();

    // Combined loading state
    const isLoading =
        isRemindersLoading ||
        isOverdueLoading ||
        isPromotionalLoading ||
        isMeetingLoading ||
        isProductChangeLoading ||
        isCustomSmsLoading ||
        isExecutingProcess ||
        processing;

    // Open form for a specific notification type
    const showTriggerForm = useCallback((formType: string) => {
        setActiveFormType(formType);
        setIsFormVisible(true);
    }, [setActiveFormType, setIsFormVisible]);

    // Close the currently open form
    const hideTriggerForm = useCallback(() => {
        setActiveFormType(null);
        setIsFormVisible(false);
        setResultSummary(null);
    }, [setActiveFormType, setIsFormVisible]);

    // Trigger loan payment reminders
    const sendLoanReminders = useCallback(async (
        daysBeforeDue: number,
        templateType?: NotificationTemplateType
    ) => {
        setProcessing(true);

        try {
            const result = await triggerLoanReminders({
                data: {
                    daysBeforeDue,
                    templateType,
                }
            });

            const remindersSent = result?.remindersSent || 0;

            showSuccessNotification(
                'Success',
                `Sent ${remindersSent} loan payment reminders`,
                dark
            );

            setResultSummary({
                // @ts-ignore
                total: remindersSent,
                // @ts-ignore
                success: remindersSent,
                timestamp: new Date().toISOString()
            });

            return remindersSent;
        } catch (error) {
            console.error('Error sending loan reminders:', error);
            showErrorNotification('Error', 'Failed to send loan reminders', dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [triggerLoanReminders, dark]);

    // Trigger loan overdue notices
    const sendLoanOverdueNotices = useCallback(async (
        daysAfterDue: number,
        templateType?: NotificationTemplateType
    ) => {
        setProcessing(true);

        try {
            const result = await triggerLoanOverdue({
                data: {
                    daysAfterDue,
                    templateType
                }
            });

            const noticesSent = result?.noticesSent || 0;

            showSuccessNotification(
                'Success',
                `Sent ${noticesSent} loan overdue notices`,
                dark
            );

            setResultSummary({
                // @ts-ignore
                total: noticesSent,
                // @ts-ignore
                success: noticesSent,
                timestamp: new Date().toISOString()
            });

            return noticesSent;
        } catch (error) {
            console.error('Error sending loan overdue notices:', error);
            showErrorNotification('Error', 'Failed to send loan overdue notices', dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [triggerLoanOverdue, dark]);

    // Send promotional notifications
    const sendPromotionalNotifications = useCallback(async (
        templateName: string,
        platform: NotificationPlatforms,
        customerFilters: Record<string, any>,
        templateParameters: Record<string, any>
    ) => {
        setProcessing(true);

        try {
            const result = await sendPromotional({
                data: {
                    templateName,
                    platform,
                    customerFilters,
                    templateParameters
                }
            });

            const notificationsSent = result?.notificationsSent || 0;

            showSuccessNotification(
                'Success',
                `Sent ${notificationsSent} promotional notifications`,
                dark
            );

            setResultSummary({
                // @ts-ignore
                total: notificationsSent,
                // @ts-ignore
                success: notificationsSent,
                timestamp: new Date().toISOString()
            });

            return notificationsSent;
        } catch (error) {
            console.error('Error sending promotional notifications:', error);
            showErrorNotification('Error', 'Failed to send promotional notifications', dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [sendPromotional, dark]);

    // Send general meeting notifications
    const sendGeneralMeetingNotifications = useCallback(async (
        meetingDate: Date,
        meetingTime: string,
        location: string,
        agenda: string,
        customerFilters: Record<string, any>
    ) => {
        setProcessing(true);

        try {
            const result = await sendMeeting({
                data: {
                    meetingDate: meetingDate.toISOString().split('T')[0],
                    meetingTime,
                    location,
                    agenda,
                    customerFilters
                }
            });

            const notificationsSent = result?.notificationsSent || 0;

            showSuccessNotification(
                'Success',
                `Sent ${notificationsSent} general meeting notifications`,
                dark
            );

            setResultSummary({
                // @ts-ignore
                total: notificationsSent,
                // @ts-ignore
                success: notificationsSent,
                timestamp: new Date().toISOString()
            });

            return notificationsSent;
        } catch (error) {
            console.error('Error sending general meeting notifications:', error);
            showErrorNotification('Error', 'Failed to send general meeting notifications', dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [sendMeeting, dark]);

    // Send product change notifications
    const sendProductChangeNotifications = useCallback(async (
        templateName: string,
        customerFilters: Record<string, any>,
        productName: string,
        effectiveDate: Date,
        changeDetails: string
    ) => {
        setProcessing(true);

        try {
            const result = await sendProductChange({
                data: {
                    templateName,
                    customerFilters,
                    productName,
                    effectiveDate: effectiveDate.toISOString().split('T')[0],
                    changeDetails
                }
            });

            const notificationsSent = result?.notificationsSent || 0;

            showSuccessNotification(
                'Success',
                `Sent ${notificationsSent} product change notifications`,
                dark
            );

            setResultSummary({
                // @ts-ignore
                total: notificationsSent,
                // @ts-ignore
                success: notificationsSent,
                timestamp: new Date().toISOString()
            });

            return notificationsSent;
        } catch (error) {
            console.error('Error sending product change notifications:', error);
            showErrorNotification('Error', 'Failed to send product change notifications', dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [sendProductChange, dark]);

    // Send custom bulk SMS or other platform notifications
    const sendCustomBulkSmsNotifications = useCallback(async (
        messageText: string,
        customerFilters: Record<string, any>,
        testMode: boolean = false,
        platform: NotificationPlatforms = NotificationPlatforms.SMS
    ) => {
        setProcessing(true);

        try {
            const result = await sendCustomSms({
                params: {
                    messageText,
                    testMode,
                    // @ts-ignore
                    platform
                },
                data: customerFilters
            });

            const notificationsSent = result?.notificationsSent || 0;

            showSuccessNotification(
                'Success',
                `Sent ${notificationsSent} custom notifications${testMode ? ' (test mode)' : ''}`,
                dark
            );

            setResultSummary({
                // @ts-ignore
                total: notificationsSent,
                // @ts-ignore
                success: notificationsSent,
                timestamp: new Date().toISOString()
            });

            return notificationsSent;
        } catch (error) {
            console.error('Error sending custom notifications:', error);
            showErrorNotification('Error', 'Failed to send custom notifications', dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [sendCustomSms, dark]);

    // Send templated notifications to custom segments
    const sendTemplatedNotifications = useCallback(async (
        templateName: string,
        platform: NotificationPlatforms,
        customerFilters: Record<string, any>,
        templateParameters: Record<string, any>,
        testMode: boolean = false
    ) => {
        setProcessing(true);

        try {
            // We'll reuse the promotional endpoint for templated messages
            const result = await sendPromotional({
                data: {
                    templateName,
                    platform,
                    customerFilters,
                    templateParameters,
                    testMode
                }
            });

            const notificationsSent = result?.notificationsSent || 0;

            showSuccessNotification(
                'Success',
                `Sent ${notificationsSent} template-based notifications${testMode ? ' (test mode)' : ''}`,
                dark
            );

            setResultSummary({
                // @ts-ignore
                total: notificationsSent,
                // @ts-ignore
                success: notificationsSent,
                timestamp: new Date().toISOString()
            });

            return notificationsSent;
        } catch (error) {
            console.error('Error sending templated notifications:', error);
            showErrorNotification('Error', 'Failed to send templated notifications', dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [sendPromotional, dark]);

    // Execute manual notification process
    const sendManualTriggeredNotifications = useCallback(async (
        serviceName: string,
        templateName: string,
        platform: NotificationPlatforms,
        language: Languages,
        filters: Record<string, any>
    ) => {
        setProcessing(true);

        try {
            const result = await executeNotificationProcess({serviceName,params:{
                    templateName,
                    platform,
                    language
            },data:filters});

            const notificationsSent = result || 0;

            showSuccessNotification(
                'Success',
                `Sent ${notificationsSent} notifications using ${serviceName}`,
                dark
            );

            setResultSummary({
                total: notificationsSent,
                success: notificationsSent,
                timestamp: new Date().toISOString()
            });

            return notificationsSent;
        } catch (error) {
            console.error(`Error executing notification process ${serviceName}:`, error);
            showErrorNotification('Error', `Failed to execute notification process: ${serviceName}`, dark);
            return 0;
        } finally {
            setProcessing(false);
        }
    }, [executeNotificationProcess, dark]);

    return {
        // State
        isLoading,
        isFormVisible,
        activeFormType,
        resultSummary,

        // Actions
        showTriggerForm,
        hideTriggerForm,
        sendLoanReminders,
        sendLoanOverdueNotices,
        sendPromotionalNotifications,
        sendGeneralMeetingNotifications,
        sendProductChangeNotifications,
        sendCustomBulkSmsNotifications,
        sendTemplatedNotifications,
        sendManualTriggeredNotifications
    };
};